import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { CreditCard, Wallet, ArrowDownLeft, ArrowUpRight, Banknote, Cable, ArrowLeftRight, FileText } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { BankCombobox } from "../components/BankCombobox";
import { Trash2, BookUser } from "lucide-react";

const CURRENCIES = ["USD", "EUR", "GBP", "NGN", "USDT", "USDC"];

function AccountCard({ icon: Icon, title, balance, onAction }) {
  return (
    <div className="rounded-xl border border-border bg-card p-6" data-testid={`account-${title.toLowerCase()}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary/10 text-primary"><Icon className="h-5 w-5" /></div>
          <div>
            <div className="text-sm font-medium">{title} Account</div>
            <div className="text-xs text-muted-foreground">Current balance</div>
          </div>
        </div>
        <div className="text-3xl font-bold tracking-tight" data-testid={`balance-${title.toLowerCase()}`}>${balance.toFixed(2)}</div>
      </div>
      <div className="mt-5 flex gap-3">
        <Button className="flex-1" onClick={() => onAction("deposit")} data-testid={`deposit-${title.toLowerCase()}`}>
          <ArrowDownLeft className="mr-1.5 h-4 w-4" /> Deposit
        </Button>
        <Button variant="outline" className="flex-1" onClick={() => onAction("withdraw")} data-testid={`withdraw-${title.toLowerCase()}`}>
          <ArrowUpRight className="mr-1.5 h-4 w-4" /> Withdraw
        </Button>
      </div>
    </div>
  );
}

export default function Accounts() {
  const [bal, setBal] = useState({ card_balance: 0, wallet_balance: 0 });
  const [dialog, setDialog] = useState(null); // {account, action}
  const [amount, setAmount] = useState("");
  const [mode, setMode] = useState("manual");
  const [busy, setBusy] = useState(false);
  const [recipient, setRecipient] = useState({ account_number: "", bank_code: "", recipient_name: "", currency: "USD" });
  const [showTransfer, setShowTransfer] = useState(false);
  const [transferAmount, setTransferAmount] = useState("");
  const [direction, setDirection] = useState("card_to_wallet");
  const [stmtMonth, setStmtMonth] = useState("");
  const [recipients, setRecipients] = useState([]);
  const [saveRecipient, setSaveRecipient] = useState(false);
  const [recipientLabel, setRecipientLabel] = useState("");

  const load = () => api.get("/accounts").then((r) => setBal(r.data)).catch(() => {});
  const loadRecipients = () => api.get("/recipients").then((r) => setRecipients(r.data.recipients)).catch(() => {});
  useEffect(() => { load(); loadRecipients(); }, []);

  const open = (account, action) => {
    setDialog({ account, action });
    setAmount("");
    setMode("manual");
    setRecipient({ account_number: "", bank_code: "", recipient_name: "", currency: "USD" });
    setSaveRecipient(false);
    setRecipientLabel("");
  };

  const applyRecipient = (id) => {
    const r = recipients.find((x) => x.id === id);
    if (r) setRecipient({ account_number: r.account_number, bank_code: r.bank_code, recipient_name: r.recipient_name, currency: r.currency });
  };

  const deleteRecipient = async (id) => {
    try {
      await api.delete(`/recipients/${id}`);
      toast.success("Recipient removed");
      loadRecipients();
    } catch {
      toast.error("Could not remove recipient");
    }
  };

  const confirm = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    if (mode === "processor" && dialog.action === "withdraw" && (!recipient.account_number || !recipient.bank_code)) {
      toast.error("Enter recipient account number and bank code"); return;
    }
    setBusy(true);
    try {
      await api.post(`/accounts/${dialog.account}/${dialog.action}`, {
        amount: amt, mode,
        account_number: recipient.account_number, bank_code: recipient.bank_code,
        recipient_name: recipient.recipient_name || "Recipient", currency: recipient.currency,
      });
      toast.success(`${dialog.action === "deposit" ? "Deposited" : "Withdrew"} $${amt.toFixed(2)}${mode === "processor" ? " via processor" : ""}`);
      if (mode === "processor" && dialog.action === "withdraw" && saveRecipient && recipientLabel.trim()) {
        try {
          await api.post("/recipients", {
            label: recipientLabel.trim(), account_number: recipient.account_number,
            bank_code: recipient.bank_code, recipient_name: recipient.recipient_name || "Recipient",
            currency: recipient.currency,
          });
          loadRecipients();
        } catch { /* non-blocking */ }
      }
      setDialog(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const confirmTransfer = async () => {
    const amt = parseFloat(transferAmount);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    setBusy(true);
    try {
      await api.post("/accounts/transfer", { amount: amt, direction });
      toast.success(`Transferred $${amt.toFixed(2)}`);
      setShowTransfer(false);
      setTransferAmount("");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const downloadStatement = async () => {
    try {
      const params = stmtMonth ? { month: stmtMonth } : {};
      const res = await api.get("/accounts/statement", { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `citidan_statement${stmtMonth ? "_" + stmtMonth.replace("-", "_") : ""}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Statement downloaded");
    } catch {
      toast.error("Could not generate statement");
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-wider text-primary">Balances & Movements</div>
          <h1 className="mt-1 text-3xl font-bold tracking-tight sm:text-4xl">Accounts</h1>
          <p className="mt-2 text-sm text-muted-foreground">Deposit, withdraw or transfer across your Card and Wallet accounts — manually or through your saved payment processor.</p>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <Button variant="outline" onClick={() => { setShowTransfer(true); setTransferAmount(""); }} data-testid="open-transfer">
            <ArrowLeftRight className="mr-1.5 h-4 w-4" /> Transfer
          </Button>
          <div className="flex items-end gap-2">
            <Input type="month" value={stmtMonth} onChange={(e) => setStmtMonth(e.target.value)} className="w-40" data-testid="statement-month" aria-label="Statement month" />
            <Button variant="outline" onClick={downloadStatement} data-testid="download-statement">
              <FileText className="mr-1.5 h-4 w-4" /> Statement
            </Button>
          </div>
        </div>
      </div>

      <div className="grid gap-5 md:grid-cols-2">
        <AccountCard icon={CreditCard} title="Card" balance={bal.card_balance} onAction={(a) => open("card", a)} />
        <AccountCard icon={Wallet} title="Wallet" balance={bal.wallet_balance} onAction={(a) => open("wallet", a)} />
      </div>

      <div className="rounded-lg border border-primary/30 bg-primary/10 px-4 py-3 text-xs text-muted-foreground">
        Processor-triggered deposits and withdrawals make <strong>live calls</strong> to your selected payment processor  (Citidan, Flutterwave, or Paystack) using the credentials saved in API Settings. Success credits/debits the balance; processor errors are recorded as failed transactions.
      </div>

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent data-testid="account-action-dialog">
          <DialogHeader>
            <DialogTitle className="capitalize">{dialog?.action} — {dialog?.account} account</DialogTitle>
            <DialogDescription>Choose how to record this movement.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Amount (USD)</Label>
              <Input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="100.00" data-testid="action-amount" autoFocus />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <button type="button" onClick={() => setMode("manual")} data-testid="mode-manual"
                className={`flex flex-col items-start gap-1 rounded-lg border p-3 text-left transition-colors ${mode === "manual" ? "border-primary bg-primary/5" : "border-border"}`}>
                <Banknote className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium">Manual entry</span>
                <span className="text-[11px] text-muted-foreground">Record the amount yourself</span>
              </button>
              <button type="button" onClick={() => setMode("processor")} data-testid="mode-processor"
                className={`flex flex-col items-start gap-1 rounded-lg border p-3 text-left transition-colors ${mode === "processor" ? "border-primary bg-primary/5" : "border-border"}`}>
                <Cable className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium">Processor trigger</span>
                <span className="text-[11px] text-muted-foreground">Use saved API credentials</span>
              </button>
            </div>
            {mode === "processor" && dialog?.action === "withdraw" && (
              <div className="space-y-3 rounded-lg border border-border p-3" data-testid="recipient-fields">
                <div className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Recipient bank details</div>
                {recipients.length > 0 && (
                  <div className="space-y-1">
                    <Label className="flex items-center gap-1.5 text-xs"><BookUser className="h-3.5 w-3.5" /> Use a saved recipient</Label>
                    <Select onValueChange={applyRecipient}>
                      <SelectTrigger data-testid="saved-recipient-select"><SelectValue placeholder="Select a saved recipient…" /></SelectTrigger>
                      <SelectContent>
                        {recipients.map((r) => (
                          <SelectItem key={r.id} value={r.id} data-testid={`recipient-opt-${r.id}`}>
                            {r.label} · {r.recipient_name || "—"} ({r.bank_code})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <Input value={recipient.account_number} onChange={(e) => setRecipient({ ...recipient, account_number: e.target.value })} placeholder="Account number" data-testid="recipient-account" />
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Payout currency</Label>
                    <Select value={recipient.currency} onValueChange={(v) => setRecipient({ ...recipient, currency: v, bank_code: "" })}>
                      <SelectTrigger data-testid="recipient-currency"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Bank / Network</Label>
                    <BankCombobox currency={recipient.currency} value={recipient.bank_code} onSelect={(code) => setRecipient({ ...recipient, bank_code: code })} />
                  </div>
                </div>
                <Input value={recipient.recipient_name} onChange={(e) => setRecipient({ ...recipient, recipient_name: e.target.value })} placeholder="Recipient name" data-testid="recipient-name" />
                <div className="flex items-center gap-2 border-t border-border pt-3">
                  <input type="checkbox" id="save-recipient" checked={saveRecipient} onChange={(e) => setSaveRecipient(e.target.checked)} className="h-4 w-4 accent-primary" data-testid="save-recipient-checkbox" />
                  <Label htmlFor="save-recipient" className="text-xs font-normal text-muted-foreground">Save this recipient for next time</Label>
                </div>
                {saveRecipient && (
                  <Input value={recipientLabel} onChange={(e) => setRecipientLabel(e.target.value)} placeholder="Recipient label (e.g. ACME Payouts)" data-testid="recipient-label" />
                )}
                {recipients.length > 0 && (
                  <div className="space-y-1 border-t border-border pt-3" data-testid="recipient-book">
                    <div className="text-[11px] text-muted-foreground">Saved recipients</div>
                    {recipients.map((r) => (
                      <div key={r.id} className="flex items-center justify-between rounded-md bg-muted/40 px-2.5 py-1.5 text-xs" data-testid={`recipient-row-${r.id}`}>
                        <span className="truncate"><span className="font-medium">{r.label}</span> · {r.bank_code} · {r.currency}</span>
                        <button type="button" onClick={() => deleteRecipient(r.id)} className="text-muted-foreground hover:text-destructive" data-testid={`recipient-delete-${r.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialog(null)}>Cancel</Button>
            <Button onClick={confirm} disabled={busy} data-testid="action-confirm">{busy ? "Processing…" : "Confirm"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showTransfer} onOpenChange={setShowTransfer}>
        <DialogContent data-testid="transfer-dialog">
          <DialogHeader>
            <DialogTitle>Transfer between accounts</DialogTitle>
            <DialogDescription>Move funds between your Card and Wallet accounts.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Direction</Label>
              <Select value={direction} onValueChange={setDirection}>
                <SelectTrigger data-testid="transfer-direction"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="card_to_wallet">Card → Wallet</SelectItem>
                  <SelectItem value="wallet_to_card">Wallet → Card</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Amount (USD)</Label>
              <Input type="number" step="0.01" value={transferAmount} onChange={(e) => setTransferAmount(e.target.value)} placeholder="100.00" data-testid="transfer-amount" autoFocus />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowTransfer(false)}>Cancel</Button>
            <Button onClick={confirmTransfer} disabled={busy} data-testid="confirm-transfer">{busy ? "Transferring…" : "Transfer"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
